<template>
 <main class="home">
    <!-- Hero Section with Carousel -->
    <section class="hero">
      <div class="carousel">
        <div class="carousel-inner" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
          <div v-for="(item, index) in heroList" 
            :key="index"
            class="carousel-item">
            <div class="carousel-background" 
              :style="{ 
                backgroundImage: `url(${baseUrl}/media${item.cover})`,
                backgroundSize: 'cover',
                backgroundPosition: '100% 100%',
                backgroundRepeat: 'no-repeat'
              }">
            </div>
            <div class="overlay"></div>
            <div class="hero-content" :key="`content-${currentSlide}-${index}`">
              <h1>{{ item.title }}</h1>
              <p>{{ item.keywords || '专业IT运维服务商 · 优质运维先行者' }}</p>
              <div class="hero-buttons" v-if="getLinksFromContent(item.content).length > 0">
                <button 
                  v-for="(link, linkIndex) in getLinksFromContent(item.content)"
                  :key="linkIndex"
                  :class="linkIndex === 0 ? 'primary-btn' : 'outline-btn'"
                  @click="handleLinkClick(link.href)"
                >
                  {{ link.text }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Carousel Controls -->
        <div class="carousel-controls">
          <div class="carousel-dots">
            <span 
              v-for="(_, index) in heroList" 
              :key="index"
              :class="{ active: currentSlide === index }"
              @click="setSlide(index)"
            ></span>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section (核心优势) -->
    <section class="features" ref="featuresSection">
      <div class="container">
        <div class="section-header" :class="{ 'fade-in': isFeaturesVisible }">
          <h2 class="section-title">
            <span class="title-text">核心优势</span>
          </h2>
          <!-- <div class="section-subtitle">专业的技术团队，完善的服务体系</div> -->
        </div>
        <div class="features-grid">
          <div class="feature-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '0.2s' }">
            <div class="feature-icon">
              <el-icon><UserFilled /></el-icon>
            </div>
            <h3>专业团队</h3>
            <p>拥有专业的IT运维团队，提供7*24小时服务保障</p>
          </div>
          <div class="feature-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '0.4s' }">
            <div class="feature-icon">
              <el-icon><Medal /></el-icon>
            </div>
            <h3>丰富经验</h3>
            <p>15年+行业经验，服务数百家客户</p>
          </div>
          <div class="feature-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '0.6s' }">
            <div class="feature-icon">
              <el-icon><SetUp /></el-icon>
            </div>
            <h3>解决方案</h3>
            <p>提供定制化解决方案，满足不同场景需求</p>
          </div>
          <div class="feature-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '0.8s' }">
            <div class="feature-icon">
              <el-icon><Service /></el-icon>
            </div>
            <h3>优质服务</h3>
            <p>快速响应，高效处理，保证服务高满意度</p>
          </div>
        </div>
        <div class="stats-grid">
          <div class="stat-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '1.0s' }">
            <h3>15年+</h3>
            <p>行业经验</p>
          </div>
          <div class="stat-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '1.2s' }">
            <h3>100+</h3>
            <p>成功案例</p>
          </div>
          <div class="stat-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '1.4s' }">
            <h3>99%</h3>
            <p>客户好评</p>
          </div>
          <div class="stat-item"
            :class="{ 'fade-in': isFeaturesVisible }"
            :style="{ animationDelay: '1.6s' }">
            <h3>7*24</h3>
            <p>技术支持</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Solutions Section (解决方案) -->
    <section class="solutions" ref="solutionsSection">
      <div class="container">
        <div class="section-header" :class="{ 'fade-in': isSolutionsVisible }">
          <h2 class="section-title">
            <span class="title-text">{{ solutionsTitle }}</span>
          </h2>
          <div class="section-subtitle">{{ solutionsDesc || '为您提供全方位的信息化服务解决方案' }}</div>
        </div>
        <div class="solutions-grid">
          <div 
            v-for="(item, index) in solutionsList" 
            :key="item.id"
            class="solution-item"
            :class="{ 'fade-in': isSolutionsVisible }"
            :style="{ animationDelay: `${0.2 * (index + 1)}s` }"
            @click="router.push(`/solutions/${getRoutePath(item.name)}`)"
          >
            <div class="solution-icon">
              <img :src="item.cover" :alt="item.name">
            </div>
            <div class="solution-content">
              <h3>{{ item.name }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Service System Section -->
    <section class="service-system" ref="serviceSystemSection">
      <div class="container">
        <div class="service-content">
          <div class="service-text" :class="{ 'animate-left': isServiceSystemVisible }">
            <div class="section-header" :class="{ 'fade-in': isServiceSystemVisible }">
              <h2 class="section-title">
                <span class="title-text">服务体系</span>
              </h2>
            </div>
            <div>服务管理制度、服务工作流程、服务作业规范、服务考核标准、服务工作手册以及服务技术手册等多规范，共同构成了完善的运维服务体系。</div>
          </div>
          <div class="service-diagram" :class="{ 'animate-right': isServiceSystemVisible }">
            <div class="service-circle">
              <!-- 中心圆 -->
              <div class="circle-center">
                <div class="center-content">
                  <h3>美音运维</h3>
                  <p>服务战略</p>
                </div>
              </div>
              <!-- 外圈元素 -->
              <div class="circle-items">
                <!-- <div class="circle-item top">
                  <span>持续改进</span>
                </div> -->
                <div class="circle-item top-right">
                  <span>服务设计</span>
                </div>
                <div class="circle-item right">
                  <span>服务标准</span>
                </div>
                <!-- <div class="circle-item bottom-right">
                  <span>专业团队</span>
                </div> -->
                <div class="circle-item bottom">
                  <span>技术经验</span>
                </div>
                <div class="circle-item bottom-left">
                  <span>服务工具</span>
                </div>
                <div class="circle-item top-right">
                  <span>专业团队</span>
                </div>
                <div class="circle-item top-left">
                  <span>持续改进</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 使用 CustomerCases 组件 -->
    <CustomerCases />

  </main>
</template>

<script setup>
	import { ref, onMounted } from 'vue'
import { UserFilled, Medal, SetUp, Service, ArrowRight } from '@element-plus/icons-vue'
import waveBg from '@/assets/images/2.png'
import CustomerCases from '@/views/court/home/components/CustomerCases.vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { columnGet2 } from "@/api/court";
import { getBaseApiUrl } from '@/utils/api-config'

const baseUrl = getBaseApiUrl() // API 基础URL
const heroList = ref([]) // 存储轮播数据
const currentSlide = ref(0)
const teamSection = ref(null)
const featuresSection = ref(null)
const solutionsSection = ref(null)
const serviceSystemSection = ref(null)
const isTeamVisible = ref(false)
const isFeaturesVisible = ref(false)
const isSolutionsVisible = ref(false)
const isServiceSystemVisible = ref(false)
const router = useRouter()
const solutionsList = ref([])
const solutionsTitle = ref('')
const solutionsDesc = ref('')

// 获取轮播数据
const getHeroData = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/article/article/`, {
      params: {
        page: 1,
        limit: 3,
        status: 'published',
        column: 22
      }
    })
    if (response.data.code === 2000) {
      heroList.value = response.data.data.data
    }
  } catch (error) {
    console.error('获取轮播数据失败:', error)
  }
}

const setSlide = (index) => {
  currentSlide.value = index
}

const startAutoPlay = () => {
  setInterval(() => {
    currentSlide.value = (currentSlide.value + 1) % heroList.value.length
  }, 5000)
}

const handleScroll = () => {
  if (featuresSection.value) {
    const rect = featuresSection.value.getBoundingClientRect()
    const triggerPoint = window.innerHeight * 0.8
    isFeaturesVisible.value = rect.top <= triggerPoint
  }
}

// 从 content 中提取链接
const getLinksFromContent = (content) => {
  if (!content) return []
  
  const links = []
  const div = document.createElement('div')
  div.innerHTML = content
  
  const anchors = div.getElementsByTagName('a')
  for (let i = 0; i < anchors.length; i++) {
    links.push({
      text: anchors[i].textContent,
      href: anchors[i].getAttribute('href')
    })
  }
  
  return links
}

// 处理按钮点击
const handleLinkClick = (href) => {
  if (href.startsWith('http')) {
    window.open(href, '_blank')
  } else {
    router.push(href)
  }
}

// 获取解决方案数据
const getSolutionsData = async () => {
  try {
    const response = await columnGet2({
      page: 1,
      limit: 10000,
      is_enable: true,
      parent: 24
    });

    if (response.data.code === 2000) {
      solutionsList.value = response.data.data.data;
    }
  } catch (error) {
    console.error('获取解决方案数据失败:', error)
  }
}

// 根据名称获取路由路径
const getRoutePath = (name) => {
  const pathMap = {
    '运行维护服务方案': 'operation',
    '硬件维保服务方案': 'hardware',
    '数据治理服务方案': 'data',
    '企业服务解决方案': 'enterprise'
  }
  return pathMap[name] || 'operation'
}

// 获取解决方案标题数据
const getSolutionsTitle = async () => {
  try {
    const response = await columnGet2({
      page: 1,
      limit: 10000,
      is_enable: true,
      id: 24
    });

    if (response.data.code === 2000) {
      // 找到 id 为 24 的数据
      const data = response.data.data.data.find(item => item.id === 24);
      if (data) {
        console.log(data)
        solutionsTitle.value = data.name;
        solutionsDesc.value = data.desc;
      }
    }
  } catch (error) {
    console.error('获取解决方案标题数据失败:', error)
  }
}

onMounted(() => {
  getHeroData() // 获取轮播数据
  startAutoPlay() // 启动自动轮播
  getSolutionsData()
  getSolutionsTitle()

  // 修改观察器配置，添加向上滑动的支持
  const observerOptions = { 
    threshold: 0.2,
    // 添加 rootMargin 使元素完全离开视口时也能触发
    rootMargin: '50px 0px'
  }

  const createObserver = (callback) => {
    return new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        callback(entry.isIntersecting)
      })
    }, observerOptions)
  }

  const teamObserver = createObserver((isIntersecting) => {
    isTeamVisible.value = isIntersecting
  })

  const featuresObserver = createObserver((isIntersecting) => {
    isFeaturesVisible.value = isIntersecting
  })

  const solutionsObserver = createObserver((isIntersecting) => {
    isSolutionsVisible.value = isIntersecting
  })

  const serviceSystemObserver = createObserver((isIntersecting) => {
    isServiceSystemVisible.value = isIntersecting
  })

  if (teamSection.value) {
    teamObserver.observe(teamSection.value)
  }
  if (featuresSection.value) {
    featuresObserver.observe(featuresSection.value)
  }
  if (solutionsSection.value) {
    solutionsObserver.observe(solutionsSection.value)
  }
  if (serviceSystemSection.value) {
    serviceSystemObserver.observe(serviceSystemSection.value)
  }

  window.addEventListener('scroll', handleScroll)
  handleScroll()
})
</script>

<style lang="scss" scoped>
	.home {
  .hero {
    height: calc(60vh - 30px);
    overflow: hidden;
    position: relative;
    margin-top: 30px;
    width: 100vw;
    left: 50%;
    transform: translateX(-50%);
    .carousel {
      height: 100%;
      width: 100%;
      position: relative;
      overflow: hidden;

      &-inner {
        display: flex;
        height: 100%;
        width: 100%;
        transition: transform 0.5s ease-in-out;
        position: relative;
        flex-wrap: nowrap;
      }

      &-item {
        position: relative;
        width: 100%;
        height: 100%;
        flex: 0 0 100%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;

        .overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0.1),
            rgba(255, 255, 255, 0.35)
          );
          z-index: 1;
        }
      }

      .carousel-background {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: bottom;
        background-repeat: no-repeat;
        transform: scale(1.02);
        will-change: transform;
      }

      .hero-content {
        position: relative;
        z-index: 2;
        text-align: center;
        padding: 0 20px;
        max-width: 850px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

        h1 {
          font-size: 48px;
          margin-bottom: 20px;
          color: #1a1a1a;
          font-weight: 600;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          text-shadow: 0 0 10px rgba(255, 255, 255, 0.8),
                     0 0 20px rgba(255, 255, 255, 0.5),
                     0 0 30px rgba(43, 93, 255, 0.5),
                     0 0 40px rgba(43, 93, 255, 0.3);
          letter-spacing: 2px;
          position: relative;

          /* 确保标题在一行显示，内容全部显示 */
          white-space: nowrap;
          overflow: visible;
          width: max-content;
          min-width: 100%;

          &::after {
            content: '';
            position: absolute;
            bottom: -5px;
            left: 0;
            width: 100%;
            height: 2px;
            background: linear-gradient(90deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.8) 50%,
              rgba(255, 255, 255, 0) 100%
            );
            filter: blur(2px);
          }
        }

        p {
          font-size: 24px;
          color: #1a1a1a;
          font-weight: bold;
          margin-bottom: 30px;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          text-shadow: 0 2px 4px rgba(250, 252, 252, 0.894);

          /* 确保关键词在一行显示，内容全部显示 */
          white-space: nowrap;
          overflow: visible;
          width: max-content;
          min-width: 100%;
        }

        .hero-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      }

      &-controls {
        position: absolute;
        bottom: 30px;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        z-index: 2;

        .carousel-dots {
          display: flex;
          gap: 10px;
          padding: 10px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(5px);
          border-radius: 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

          span {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: rgba(43, 93, 255, 0.4);
            cursor: pointer;
            transition: all 0.3s;

            &.active {
              background: #2b5dff;
              transform: scale(1.2);
              box-shadow: 0 0 10px rgba(43, 93, 255, 0.3);
            }
          }
        }
      }
    }
  }

  .features {
    padding: 80px 0;
    background: #fff;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 30px;
      text-align: center;
      margin-bottom: 50px;

      .stat-item {
        h3 {
          font-size: 36px;
          color: #2b5dff;
          font-weight: 600;
          margin-bottom: 10px;
        }

        p {
          font-size: 16px;
          color: #666;
        }
      }
    }

    .section-header {
      text-align: center;
      margin-bottom: 20px;
      &.fade-in {
        animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }

      h2 {
        font-size: 36px;
        color: #1a1a1a;
        margin-bottom: 20px;
        font-weight: 600;
      }

      p {
        font-size: 18px;
        color: #666;
      }
    }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 30px;
      margin-bottom: 50px;
    }

    .feature-item {
      text-align: center;
      padding: 30px 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      opacity: 0;

      &.fade-in {
        animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }

      &:hover {
        transform: translateY(-8px);
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
      }

      .feature-icon {
        width: 72px;
        height: 72px;
        margin: 0 auto 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #e6f0ff 0%, #f0f7ff 100%);
        border-radius: 50%;

        :deep(.el-icon) {
          font-size: 32px;
          color: #2b5dff;
        }
      }

      h3 {
        font-size: 20px;
        color: #1a1a1a;
        font-weight: 600;
        margin-bottom: 15px;
      }

      p {
        font-size: 14px;
        color: #666;
        line-height: 1.6;
      }
    }

    .stat-item {
      opacity: 0;

      &.fade-in {
        animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }

      h3 {
        font-size: 36px;
        color: #2b5dff;
        font-weight: 600;
        margin-bottom: 10px;
      }

      p {
        font-size: 16px;
        color: #666;
      }
    }
  }

  .solutions {
    padding: 80px 0;
    background: #f8f9fa;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .section-header {
      text-align: center;
      margin-bottom: 60px;
      &.fade-in {
        animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }

      h2 {
        font-size: 36px;
        color: #333;
        margin-bottom: 20px;
      }

      p {
        font-size: 18px;
        color: #666;
      }
    }

    .solutions-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 30px;
    }

    .solution-item {
      background: #fff;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      opacity: 0;
      cursor: pointer;

      &.fade-in {
        animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);

        .solution-icon img {
          transform: scale(1.05);
        }
      }

      .solution-icon {
        width: 100%;
        height: 200px;
        overflow: hidden;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
      }

      .solution-content {
        padding: 24px;
        background: #fff;

        h3 {
          font-size: 20px;
          color: #1a1a1a;
          font-weight: 600;
          margin-bottom: 12px;
        }

        p {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
          margin: 0;
          height: 44px; // 固定高度，显示两行
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }
    }

    // 响应式布局
    @media (max-width: 1200px) {
      .solutions-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
      }
    }

    @media (max-width: 576px) {
      .solutions-grid {
        grid-template-columns: 1fr;
      }

      .solution-item .solution-icon {
        height: 160px;
      }
    }
  }

  .service-system {
    padding: 80px 0;
    background: #fff;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .service-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      align-items: center;
    }

    .service-text {
      opacity: 0;
      transform: translateX(-100px);
      transition: opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1),
                 transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: transform, opacity;

      &.animate-left {
        opacity: 1;
        transform: translateX(0);
      }

      .section-header {
        text-align: left;
        margin-bottom: 10px;

        .section-subtitle {
          max-width: 600px;
        }
      }

      h2 {
        font-size: 36px;
        color: #1a1a1a;
        margin-bottom: 20px;
        font-weight: 600;
      }

      div {
        color: #666;
        font-size: 16px;
        line-height: 1.6;
      }
    }

    .service-diagram {
      opacity: 0;
      transform: translateX(100px);
      transition: opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1),
                 transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: transform, opacity;

      &.animate-right {
        opacity: 1;
        transform: translateX(0);
      }

      .service-circle {
        width: 500px;
        height: 500px;
        margin: 0 auto;
        position: relative;

        .circle-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 160px;
          height: 160px;
          background: linear-gradient(135deg, #2b5dff 0%, #1a45e0 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          z-index: 2;
          box-shadow: 0 8px 30px rgba(43, 93, 255, 0.3);

          .center-content {
            text-align: center;

            h3 {
              font-size: 22px;
              margin-bottom: 8px;
            }

            p {
              font-size: 16px;
              opacity: 0.9;
            }
          }
        }

        .circle-items {
          position: absolute;
          width: 100%;
          height: 100%;
          animation: rotate 60s linear infinite;

          .circle-item {
            position: absolute;
            width: 100px;
            height: 100px;
            background: #fff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
            animation: counter-rotate 60s linear infinite;
            transition: transform 0.3s ease, box-shadow 0.3s ease;

            &:hover {
              transform: translateY(-5px) rotate(0deg);
              box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
            }

            span {
              font-size: 16px;
              color: #333;
              font-weight: 500;
            }

            &.top { top: 0; left: 50%; transform: translateX(-50%); }
            &.top-right { top: 15%; right: 15%; }
            &.right { top: 50%; right: 0; transform: translateY(-50%); }
            &.bottom-right { bottom: 15%; right: 15%; }
            &.bottom { bottom: 0; left: 50%; transform: translateX(-50%); }
            &.bottom-left { bottom: 15%; left: 15%; }
            &.left { top: 50%; left: 0; transform: translateY(-50%); }
            &.top-left { top: 15%; left: 15%; }
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .hero {
      height: calc(50vh - 30px);
      margin-top: 60px;
    }

    .features {
      .features-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
      }

      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        margin-bottom: 40px;
      }
    }

    .solutions {
      .solutions-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
      }
    }

    .service-system {
      .service-content {
        grid-template-columns: 1fr;
        gap: 40px;
        text-align: center;
      }

      .service-circle {
        width: 300px;
        height: 300px;

        .circle-center {
          width: 100px;
          height: 100px;

          .center-content {
            h3 {
              font-size: 18px;
            }
            p {
              font-size: 14px;
            }
          }
        }

        .circle-items {
          .circle-item {
            width: 60px;
            height: 60px;

            span {
              font-size: 12px;
            }
          }
        }
      }
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 按钮样式
.primary-btn {
  padding: 14px 36px;
  font-size: 16px;
  color: #fff;
  background: linear-gradient(135deg, #2b5dff 0%, #1a45e0 100%);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  letter-spacing: 0.5px;

  &:hover {
    background: linear-gradient(135deg, #1a45e0 0%, #0931c3 100%);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(43, 93, 255, 0.3);
  }
}

.outline-btn {
  padding: 14px 36px;
  font-size: 16px;
  color: #2b5dff;
  background: transparent;
  border: 2px solid #2b5dff;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  letter-spacing: 0.5px;

  &:hover {
    background: rgba(43, 93, 255, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(43, 93, 255, 0.15);
  }
}

@media (max-width: 576px) {
  .home {
    .solutions {
      .solutions-grid {
        grid-template-columns: 1fr;
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes counter-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
}

// 添加淡入淡出动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 公共标题样式
.section-header {
  text-align: center;
  margin-bottom: 60px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;

  &.fade-in {
    opacity: 1;
    transform: translateY(0);
  }

  .section-title {
    position: relative;
    display: inline-block;
    margin-bottom: 16px;

    .title-text {
      position: relative;
      z-index: 1;
      font-size: 36px;
      font-weight: 600;
      color: var(--el-text-color-primary);
      
      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 16px;
        background: linear-gradient(90deg, rgba(64, 158, 255, 0.3) 0%, rgba(64, 158, 255, 0) 100%);
        z-index: -1;
      }
    }
  }

  .section-subtitle {
    font-size: 16px;
    color: var(--el-text-color-secondary);
    margin-top: 8px;
  }
}

// 添加一个发光动画
@keyframes glow {
  0% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.8),
                0 0 20px rgba(255, 255, 255, 0.5),
                0 0 30px rgba(43, 93, 255, 0.5),
                0 0 40px rgba(43, 93, 255, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.8),
                0 0 30px rgba(255, 255, 255, 0.5),
                0 0 40px rgba(43, 93, 255, 0.5),
                0 0 50px rgba(43, 93, 255, 0.3);
  }
  100% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.8),
                0 0 20px rgba(255, 255, 255, 0.5),
                0 0 30px rgba(43, 93, 255, 0.5),
                0 0 40px rgba(43, 93, 255, 0.3);
  }
}
</style>